"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnitTarget = exports.matchesUnitTargetItems = exports.matchesUnitTarget = exports.normalizeUnitTarget = exports.parseUnitTarget = exports.UNIT_TARGET_ALL = void 0;
const zod_1 = require("zod");
const unit_1 = require("./unit");
// SBCSYNC O21 / RTPE O7 — цель: НА КАКИХ юнитах оси должна существовать сущность.
//
// У транка контур ровно один (`sbc_unit_id` — идентичность перед провайдером).
// У SIP-точки контура нет — есть ЦЕЛЬ: список через запятую из элементов
//   `*`          — все юниты, включая те, которых сегодня нет;
//   `<id>`       — ровно один юнит (по regex `UnitId`);
//   `<prefix>*`  — все юниты, чей id начинается с prefix (только хвостовой `*`,
//                  без regex и отрицаний).
// Юнит X при pull'е снимка берёт точки, чья цель ему соответствует. «Зоны»
// (`sbc-msk-*`) — это именование юнитов, платформа о них не знает, поэтому
// цель `prefix*` НЕ разворачивается в список при записи — она матчится в момент
// сборки снимка. Отсюда: одна строка на аккаунт `(domain, username)`, а ревизия
// и покрытие доменов у точек глобальные (псевдо-строка `'*'` в
// `core.sbc_unit_revisions`).
//
// Модуль browser-safe (пакет импортит `apps/ui`): чистые функции, без `node:*`.
/**
 * `'*'` — одно значение в трёх ролях (O21): цель «все контуры»; `sbcUnitId` в
 * `evt.sbc.desired.changed` после мутации SIP-точки (ревизия точек глобальна);
 * псевдо-строка `sbc_unit_id` в `core.sbc_unit_revisions`, где живут ревизия и
 * `covered_domains` точек. И цель политики контура `rtpeTarget`/`cuTarget`
 * (RTPE O7). Core и юнит импортируют отсюда, а не дублируют литерал.
 */
exports.UNIT_TARGET_ALL = '*';
/**
 * Prefix перед хвостовым `*` — та же грамматика, что у `UnitId`, но не длиннее
 * 63: `UnitId` ≤ 64, и prefix из 64 символов не матчил бы ни один id, кроме
 * ровно себя, — валидную, но мёртвую цель пропускать нельзя.
 */
const PREFIX_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
const PREFIX_MAX_LENGTH = 63;
const parseItem = (item) => {
    if (item === exports.UNIT_TARGET_ALL)
        return { kind: 'all' };
    if (item.endsWith('*')) {
        const prefix = item.slice(0, -1);
        if (PREFIX_PATTERN.test(prefix) && prefix.length <= PREFIX_MAX_LENGTH)
            return { kind: 'prefix', prefix };
        throw new Error(`unit target: недопустимый элемент «${item}» — перед «*» ожидается prefix вида [a-z0-9][a-z0-9_-]* не длиннее ${PREFIX_MAX_LENGTH}`);
    }
    if (unit_1.UnitId.safeParse(item).success)
        return { kind: 'exact', id: item };
    throw new Error(`unit target: недопустимый элемент «${item}» — ожидается «*», <id> или <prefix>* (только хвостовой «*»)`);
};
/**
 * Разобрать цель. Режет по `,`, тримит элементы; пустой элемент и дубль
 * (после trim) — ошибка с текстом, называющим элемент. Наличие `*` делает
 * остальные элементы избыточными, но это НЕ ошибка — `normalizeUnitTarget`
 * схлопнет их в `'*'`.
 *
 * @example parseUnitTarget('sbc-1,sbc-msk-*')
 *   → [{ kind: 'exact', id: 'sbc-1' }, { kind: 'prefix', prefix: 'sbc-msk-' }]
 */
const parseUnitTarget = (raw) => {
    const seen = new Set();
    return raw.split(',').map((part) => {
        const item = part.trim();
        if (item === '')
            throw new Error(`unit target: пустой элемент в «${raw}»`);
        if (seen.has(item))
            throw new Error(`unit target: дубль элемента «${item}» в «${raw}»`);
        seen.add(item);
        return parseItem(item);
    });
};
exports.parseUnitTarget = parseUnitTarget;
const itemToString = (item) => {
    switch (item.kind) {
        case 'all': return exports.UNIT_TARGET_ALL;
        case 'exact': return item.id;
        case 'prefix': return `${item.prefix}*`;
    }
};
/**
 * Каноническая форма цели — единственная, которая хранится и сравнивается.
 * `'*'`, если есть элемент all (остальное избыточно); иначе элементы без
 * пробелов, уникальные, отсортированные, через `,`.
 *
 * @example normalizeUnitTarget('sbc-msk-*, sbc-1') → 'sbc-1,sbc-msk-*'
 * @example normalizeUnitTarget('sbc-1, *') → '*'
 */
const normalizeUnitTarget = (raw) => {
    const items = (0, exports.parseUnitTarget)(raw);
    if (items.some((item) => item.kind === 'all'))
        return exports.UNIT_TARGET_ALL;
    return [...new Set(items.map(itemToString))].sort().join(',');
};
exports.normalizeUnitTarget = normalizeUnitTarget;
/**
 * Соответствует ли юнит `unitId` цели `target`.
 *
 * `prefix*` — обычный glob: матчит любой id, начинающийся с prefix, ВКЛЮЧАЯ
 * ровно prefix. `sbc-msk*` матчит и `sbc-msk-1`, и `sbc-msk`; `sbc-msk-*` —
 * `sbc-msk-1`, но не `sbc-msk` (дефис входит в prefix).
 *
 * Бросает на невалидной цели: в БД лежит только нормализованная форма
 * (CHECK миграции 125), значит невалидная строка — запись мимо продуктового пути.
 */
const matchesUnitTarget = (target, unitId) => (0, exports.matchesUnitTargetItems)((0, exports.parseUnitTarget)(target), unitId);
exports.matchesUnitTarget = matchesUnitTarget;
/**
 * Для hot-path — матч по уже разобранной цели: парсить один раз при компиляции
 * (`compileRoutes` inbound-правил), а не на каждом входящем × N правил.
 */
const matchesUnitTargetItems = (items, unitId) => items.some((item) => {
    switch (item.kind) {
        case 'all': return true;
        case 'exact': return item.id === unitId;
        case 'prefix': return unitId.startsWith(item.prefix);
    }
});
exports.matchesUnitTargetItems = matchesUnitTargetItems;
/**
 * Цель — множество юнитов одной оси: SIP-точка → контуры, политика контура →
 * rtpe/cu (RTPE O7). Сообщение ошибки — из парсера, оно называет элемент.
 */
exports.UnitTarget = zod_1.z
    .string()
    .min(1)
    .max(256)
    .superRefine((value, ctx) => {
    try {
        (0, exports.parseUnitTarget)(value);
    }
    catch (err) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: err instanceof Error ? err.message : String(err),
        });
    }
});
//# sourceMappingURL=unit-target.js.map